import { useAuth } from '@/stores/use-auth';
import { isJwtExpiredOrExpiringSoon } from '@/lib/jwt-client';

/**
 * Bearer token for API calls: in-memory Zustand first (sync), then persisted localStorage, then E2E.
 * OAuth uses HttpOnly cookies, but cross-origin fetch may not send them; OAuth callback also sets
 * ?access_token=… (legacy: #fragment) so we can populate the store before the first /users/me call.
 *
 * Expired tokens are omitted so the server can authenticate via a fresher HttpOnly access_token cookie
 * (for example after another browser tab refreshed the session).
 */
export function getBearerTokenSync(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const e2e = (window as { __E2E_TOKEN__?: string }).__E2E_TOKEN__;
  if (e2e) {
    return e2e;
  }
  const fromStore = useAuth.getState().token;
  if (fromStore && !isJwtExpiredOrExpiringSoon(fromStore)) {
    return fromStore;
  }
  try {
    const authStorage = window.localStorage.getItem('auth-storage');
    if (authStorage) {
      const parsed = JSON.parse(authStorage) as { state?: { token?: string | null } };
      const persisted = parsed?.state?.token ?? null;
      if (persisted && !isJwtExpiredOrExpiringSoon(persisted)) {
        return persisted;
      }
    }
  } catch {
    // ignore
  }
  return null;
}
